import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useMatch } from 'react-router-dom';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import {
    MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_1,
    MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_2,
    MIN_WINE_BOTTLE_QTY_TO_SHIP,
    MIN_WINE_BOTTLE_QTY_TO_WAIVE_FEE,
    WINE_PERCENT_DISCOUNT_TIER_1,
    WINE_PERCENT_DISCOUNT_TIER_2,
} from 'constants/shop';
import CartButton from 'features/shop/cart/CartButton';
import { OrderMinProgressReveal } from 'features/shop/cart/CartSheet/OrderMinProgressReveal';
import { useHideCartSheet } from 'features/shop/cart/CartSheet/useHideCartSheet';
import { getCartSheetVisibility } from 'features/shop/cart/CartSheet/utils';
import PlusMembershipOptInPill, { usePlusMembershipOptInPill } from 'features/shop/cart/PlusMembershipOptInPill';
import FreeShippingProgress from 'features/shop/FreeShippingProgress';
import FrozenFeeWaiveProgress from 'features/shop/FrozenFeeWaiveProgress/FrozenFeeWaiveProgress';
import OrderMinProgress from 'features/shop/OrderMinProgress';
import { getHeaderHeight } from 'features/site/header/utils';
import { WinePLPIcon } from 'honeydew/icons/Wine';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook/useAccountPlusMembershipHook';
import useGlobalStore from 'hooks/useGlobalStore';
import useOrderMin from 'hooks/useOrderMin';
import { plulurizeWord } from 'utils/globalUtils';
import { filterPerkSelections, getFrozenItems, getGroceryItems, getTotalWineBottleQty } from 'utils/orderUtils';
import { getProductsSubtotal, getWaiveFrozenFee, PriceType } from 'utils/priceUtils';

/**
 * Row below the product area for wine: explains bottle thresholds (ship minimum, fee waiver,
 * percent-off tiers) and offers a wine-themed control that toggles the cart sheet.
 *
 * @param {Object} props
 * @param {number} props.totalWineBottleQty - Count of wine bottles in the current selections.
 * @param {boolean} props.unbundleWineFeatureFlag - Account flag enabling tiered wine discount copy.
 * @param {number} props.winePercentOff - Best applicable wine discount percent from selections.
 * @returns {import('react').JSX.Element} Wine status strip with bottle count control.
 */
function WineCartSheet({ totalWineBottleQty, unbundleWineFeatureFlag, winePercentOff }) {
    const { setCartOpen } = useGlobalStore();

    const [prevBottleAmt, setPrevBottleAmt] = useState();
    const [transitionMsg, setTransitionMsg] = useState('');

    const bottlesToMeetMin = MIN_WINE_BOTTLE_QTY_TO_SHIP - totalWineBottleQty;
    const bottlesToWaiveFee = MIN_WINE_BOTTLE_QTY_TO_WAIVE_FEE - totalWineBottleQty;
    const bottlesFor10OFF = MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_1 - totalWineBottleQty;
    const bottlesFor15OFF = MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_2 - totalWineBottleQty;
    const wineMinMet = totalWineBottleQty >= MIN_WINE_BOTTLE_QTY_TO_SHIP;

    useEffect(() => {
        if (typeof prevBottleAmt !== 'undefined' && prevBottleAmt > 0 && prevBottleAmt < totalWineBottleQty) {
            if (
                prevBottleAmt < MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_2 &&
                totalWineBottleQty >= MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_2 &&
                unbundleWineFeatureFlag
            ) {
                setTransitionMsg(`${WINE_PERCENT_DISCOUNT_TIER_2}%OFF discount applied!`);
            } else if (
                prevBottleAmt < MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_1 &&
                totalWineBottleQty >= MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_1 &&
                unbundleWineFeatureFlag
            ) {
                setTransitionMsg(`${WINE_PERCENT_DISCOUNT_TIER_1}%OFF discount applied!`);
            } else if (
                prevBottleAmt < MIN_WINE_BOTTLE_QTY_TO_WAIVE_FEE &&
                totalWineBottleQty >= MIN_WINE_BOTTLE_QTY_TO_WAIVE_FEE
            ) {
                setTransitionMsg('Your Alcohol Service Fee is waived!');
            } else if (prevBottleAmt < MIN_WINE_BOTTLE_QTY_TO_SHIP && wineMinMet) {
                setTransitionMsg('Wine order minimum met!');
            }
        }
        setPrevBottleAmt(totalWineBottleQty);
    }, [totalWineBottleQty]);

    useEffect(() => {
        if (transitionMsg !== '') {
            setTimeout(() => {
                setTransitionMsg('');
            }, 3000);
        }
    }, [transitionMsg]);

    return (
        <div data-testid="wine-cart-sheet" className="bg-malt-off-white flex w-full justify-between p-10">
            {transitionMsg !== '' ? (
                <p className="text-body-sm pt-[2px] font-grotesk-bold text-success">{transitionMsg}</p>
            ) : (
                <p className="text-body-sm pt-[2px]">
                    {totalWineBottleQty < MIN_WINE_BOTTLE_QTY_TO_WAIVE_FEE ? (
                        <span>
                            {totalWineBottleQty < MIN_WINE_BOTTLE_QTY_TO_SHIP ? (
                                <>
                                    Add{' '}
                                    <span className="font-grotesk-bold text-error">
                                        {bottlesToMeetMin} {plulurizeWord('bottle', bottlesToMeetMin)}
                                    </span>{' '}
                                    to ship Wine order
                                </>
                            ) : (
                                <>
                                    Add{' '}
                                    <span className="font-grotesk-bold text-success">
                                        {bottlesToWaiveFee} {plulurizeWord('bottle', bottlesToWaiveFee)}
                                    </span>{' '}
                                    to waive Alcohol Service Fee
                                </>
                            )}
                        </span>
                    ) : (
                        <span>
                            {unbundleWineFeatureFlag ? (
                                <span>
                                    {totalWineBottleQty < MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_1 ? (
                                        <>
                                            Add{' '}
                                            <span className="font-grotesk-bold text-success">
                                                {bottlesFor10OFF} {plulurizeWord('bottle', bottlesFor10OFF)}
                                            </span>{' '}
                                            to save {WINE_PERCENT_DISCOUNT_TIER_1}%
                                        </>
                                    ) : (
                                        <span>
                                            {totalWineBottleQty < MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_2 ? (
                                                <>
                                                    Add{' '}
                                                    <span className="font-grotesk-bold text-success">
                                                        {bottlesFor15OFF} {plulurizeWord('bottle', bottlesFor15OFF)}
                                                    </span>{' '}
                                                    to save {WINE_PERCENT_DISCOUNT_TIER_2}%
                                                </>
                                            ) : (
                                                <>
                                                    Wine order{' '}
                                                    <span className="font-grotesk-bold text-success">
                                                        {winePercentOff}%OFF
                                                    </span>{' '}
                                                </>
                                            )}
                                        </span>
                                    )}
                                </span>
                            ) : (
                                <span className="font-grotesk-bold text-success">
                                    Your Alcohol Service Fee is waived!
                                </span>
                            )}
                        </span>
                    )}
                </p>
            )}
            <button
                className={clsx('flex rounded-full border-2 pt-[2px] pr-[17px] pb-[2px] pl-[12px]', {
                    'bg-white': totalWineBottleQty < MIN_WINE_BOTTLE_QTY_TO_SHIP,
                    'border-kale bg-kale text-white if:border-beet if:bg-beet': wineMinMet,
                })}
                onClick={() => setCartOpen((currVal) => !currVal)}
                type="button"
            >
                <span className="mr-5">
                    <WinePLPIcon height={23} width={14} />
                </span>
                <p className="text-body-sm font-grotesk-bold">{totalWineBottleQty}</p>
            </button>
        </div>
    );
}

/**
 * Grocery cart sheet: order minimum (optionally in a reveal on the frozen aisle), free shipping
 * progress, and frozen-fee waive progress.
 *
 * @param {Object} props
 * @param {boolean} props.isFrozenAisleCartSheet - On frozen PLP, show order-min in a reveal and
 *   always show frozen-fee row when a waive threshold exists.
 * @param {boolean} props.showFreeShippingProgress - Whether the free-shipping bar is visible
 *   (per parent `getCartSheetVisibility` / hide flags).
 * @param {boolean} props.showFrozenFeeWaiveToast - Whether the post–add-to-cart frozen waive strip
 *   is visible (per parent visibility + hide-frozen-toast).
 * @param {boolean} props.showOrderMinProgress - Whether the order-minimum bar is visible
 *   (per parent `getCartSheetVisibility` / hide flags).
 * @returns {import('react').JSX.Element | false} The white status panel, or `false` if nothing
 *   should show.
 */
function GroceryCartSheet({
    isFrozenAisleCartSheet,
    showFreeShippingProgress,
    showFrozenFeeWaiveToast,
    showOrderMinProgress,
}) {
    if (!isFrozenAisleCartSheet && !showOrderMinProgress && !showFreeShippingProgress && !showFrozenFeeWaiveToast)
        return false;

    const stackedFrozen = showOrderMinProgress || showFreeShippingProgress;

    return (
        <div data-testid="grocery-cart-sheet" className="w-full bg-white p-10">
            {isFrozenAisleCartSheet ? (
                <OrderMinProgressReveal show={showOrderMinProgress}>
                    <OrderMinProgress />
                </OrderMinProgressReveal>
            ) : (
                showOrderMinProgress && <OrderMinProgress />
            )}
            {showFreeShippingProgress && <FreeShippingProgress />}
            {(showFrozenFeeWaiveToast || isFrozenAisleCartSheet) && (
                <FrozenFeeWaiveProgress isFrozenAisleRoute={isFrozenAisleCartSheet} stacked={stackedFrozen} />
            )}
        </div>
    );
}

/**
 * Presents the floating cart “sheet” UI: optional wine and grocery status rows, mobile cart
 * button and M+ opt-in, and desktop M+ opt-in. Visibility of each block depends on cart
 * state and `hide*CartSheet` flags, plus frozen-fee progress when applicable.
 *
 * @param {Object} props
 * @param {number} props.groceryItemsTotalCost - Used by `getCartSheetVisibility` for grocery row logic.
 * @param {boolean} [props.hasUpSellsCarousel=false] - Affects mobile positioning when an upsell
 *   carousel is present.
 * @param {boolean} props.hideFrozenToast - Hides the frozen-fee waive “toast” after dismiss logic.
 * @param {boolean} props.hideGroceryCartSheet - Auto-hides the grocery sub-sheet (order min / free
 *   ship) after milestones unless frozen waive overrides.
 * @param {boolean} props.hideWineCartSheet - Auto-hides the wine sub-sheet.
 * @param {boolean} [props.isFrozenAisleCartSheet=false] - Renders frozen-aisle order-min reveal and
 *   frozen fee progress even when the standard grocery row would be off.
 * @param {boolean} [props.mobile=false] - Stacked full-width + rounded sheet corners (mobile) vs
 *   desktop placement in the header.
 * @param {(() => void) | undefined} [props.onPlusMembershipPillDismiss] - Fired when the M+ opt-in pill
 *   is dismissed.
 * @param {ReactNode | null} [props.plusMembershipOptInPillMessage=null] - CTA from
 *   `usePlusMembershipOptInPill` when the pill is shown.
 * @param {string | null} [props.plusMembershipOptInPillMessageVariant=null] - Copy branch id
 *   (`data-plus-membership-opt-in-pill-message-variant` / Amplitude).
 * @param {boolean} [props.productAdded=false] - Drives whether wine/grocery strips may show
 *   (post add-to-cart flash window).
 * @param {boolean} props.showFreeShippingProgress - Whether free shipping progress should be
 *   considered (parent-computed; may still be suppressed by visibility helpers).
 * @param {boolean} props.showFrozenFeeWaiveToast - Whether the frozen post-add strip is allowed
 *   (parent: product added, order min met, frozen context, etc.).
 * @param {boolean} props.showOrderMinProgress - Whether order-minimum progress is requested.
 * @param {boolean} [props.showPlusMembershipOptInPill=false] - M+ in-your-face: feature on, not dismissed, and
 *   non-null message.
 * @param {number} props.totalWineBottleQty
 * @param {boolean} props.unbundleWineFeatureFlag
 * @param {number} props.winePercentOff
 * @returns {import('react').JSX.Element} Sheet shell with zero or more inner strips and cart
 *   affordances.
 */
export function CartSheetContent({
    groceryItemsTotalCost,
    hasUpSellsCarousel = false,
    hideFrozenToast,
    hideGroceryCartSheet,
    hideWineCartSheet,
    isFrozenAisleCartSheet = false,
    mobile = false,
    onPlusMembershipPillDismiss,
    plusMembershipOptInPillMessage = null,
    plusMembershipOptInPillMessageVariant = null,
    productAdded = false,
    showFreeShippingProgress,
    showFrozenFeeWaiveToast,
    showOrderMinProgress,
    showPlusMembershipOptInPill = false,
    totalWineBottleQty,
    unbundleWineFeatureFlag,
    winePercentOff,
}) {
    const { grocerySheet, showGrocerySheet, showWineSheet } = getCartSheetVisibility({
        productAdded,
        totalWineBottleQty,
        hideWineCartSheet,
        hideGroceryCartSheet,
        hideFrozenToast,
        groceryItemsTotalCost,
        showOrderMinProgress,
        showFreeShippingProgress,
        showFrozenFeeWaiveToast,
    });

    return (
        <div
            data-testid="cart-sheet"
            className={clsx('z-1100 transition-all', {
                'w-full': mobile,
                'fixed right-40 -z-10 flex w-[375px] flex-col items-end gap-10': !mobile,
                'bottom-0': hasUpSellsCarousel && mobile,
                'fixed bottom-0': !hasUpSellsCarousel && mobile,
            })}
            style={!mobile ? { top: getHeaderHeight() } : undefined}
        >
            <div
                className={clsx('shadow-base w-full', {
                    '[&>div:first-child]:rounded-t-lg': mobile && !hasUpSellsCarousel,
                    'rounded-lg [&>div:last-child]:rounded-b-lg': mobile,
                    'rounded-b-lg [&>div:last-child]:rounded-b-lg': !mobile,
                })}
            >
                {showWineSheet && (
                    <WineCartSheet
                        totalWineBottleQty={totalWineBottleQty}
                        unbundleWineFeatureFlag={unbundleWineFeatureFlag}
                        winePercentOff={winePercentOff}
                    />
                )}
                {(showGrocerySheet || isFrozenAisleCartSheet) && (
                    <GroceryCartSheet
                        isFrozenAisleCartSheet={isFrozenAisleCartSheet}
                        showFreeShippingProgress={grocerySheet.freeShippingProgress}
                        showFrozenFeeWaiveToast={grocerySheet.frozenFeeWaive}
                        showOrderMinProgress={grocerySheet.orderMinProgress}
                    />
                )}
                {mobile && (
                    <div
                        className={clsx({
                            'grid w-full min-w-0 grid-cols-2 items-end gap-10 bg-transparent px-10':
                                showPlusMembershipOptInPill,
                            'mb-20':
                                showPlusMembershipOptInPill &&
                                !showWineSheet &&
                                !showGrocerySheet &&
                                !isFrozenAisleCartSheet,
                            'bg-white': !showPlusMembershipOptInPill,
                            'px-10 pb-10':
                                !showPlusMembershipOptInPill &&
                                (showWineSheet || showGrocerySheet || isFrozenAisleCartSheet),
                            'mx-10 mb-20 rounded-full!':
                                !showPlusMembershipOptInPill &&
                                !showWineSheet &&
                                !showGrocerySheet &&
                                !isFrozenAisleCartSheet,
                            'py-10':
                                showPlusMembershipOptInPill &&
                                (showWineSheet || showGrocerySheet || isFrozenAisleCartSheet),
                        })}
                    >
                        <CartButton splitWithSavingsPill={showPlusMembershipOptInPill} />
                        {showPlusMembershipOptInPill && (
                            <PlusMembershipOptInPill
                                layout="mobileWeb"
                                message={plusMembershipOptInPillMessage}
                                messageVariant={plusMembershipOptInPillMessageVariant}
                                onDismiss={onPlusMembershipPillDismiss}
                            />
                        )}
                    </div>
                )}
            </div>
            {!mobile && showPlusMembershipOptInPill && (
                <PlusMembershipOptInPill
                    layout="desktop"
                    message={plusMembershipOptInPillMessage}
                    messageVariant={plusMembershipOptInPillMessageVariant}
                    onDismiss={onPlusMembershipPillDismiss}
                />
            )}
        </div>
    );
}

/**
 * Loads account and selections, derives which progress rows, frozen-fee messaging, and M+ test UI
 * to show, and auto-hides the wine and grocery sub-sheets after order milestones. Renders nothing
 * until data is ready.
 *
 * @param {Object} props
 * @param {boolean} [props.mobile] - Stacked, full-width layout and rounded sheet corners vs desktop placement.
 * @param {boolean} [props.orderMinOnly] - If true, suppresses free-shipping progress in favor of order minimum only.
 * @returns {ReactNode} The sheet content, or `false` while `account`/`selections` are loading.
 */
function CartSheet({ mobile = false, orderMinOnly = false }) {
    const { plusMembershipEligibleOrder } = useAccountPlusMembershipHook();
    const {
        dismissPlusMembershipOptInPill,
        plusMembershipOptInPillMessage,
        plusMembershipOptInPillMessageVariant,
        showPlusMembershipOptInPill,
    } = usePlusMembershipOptInPill();

    const { data: account } = useAccountQuery();
    const { data: selections } = useSelectionsQuery();
    const { productAdded, showFrozenFeeProgressInCartToast } = useGlobalStore();

    const isFrozenAisleRoute = !!useMatch('/shop/aisle/frozen/all');

    const { freeShippingEligible = false, frozenFeeWaiveThreshold } = account.nextOrder.cartExperience ?? {};
    const { unbundledWine: unbundleWineFeatureFlag } = account?.featureFlags ?? {};
    const winePercentOff = selections?.alcoholDiscountPercentage;

    const { isOrderMinMet } = useOrderMin();
    const groceryItems = getGroceryItems(selections) ?? [];

    const filteredSelections = useMemo(() => (selections ? filterPerkSelections(selections) : null), [selections]);
    const frozenItems = filteredSelections ? getFrozenItems(filteredSelections) : [];

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;

    const waiveFrozenFee = getWaiveFrozenFee({
        account,
        options: { priceType },
        selections: filteredSelections,
    });

    const showFrozenFeeWaiveToast =
        productAdded &&
        isOrderMinMet &&
        showFrozenFeeProgressInCartToast &&
        frozenFeeWaiveThreshold != null &&
        frozenItems.length > 0;

    const groceryItemsTotalCost = getProductsSubtotal({
        options: { priceType },
        products: groceryItems,
    });

    const totalWineBottleQty = getTotalWineBottleQty(selections);
    const showOrderMinProgress =
        groceryItems?.length > 0 && (orderMinOnly || !isOrderMinMet || (isOrderMinMet && !freeShippingEligible));
    const showFreeShippingProgress = groceryItems?.length > 0 && isOrderMinMet && freeShippingEligible && !orderMinOnly;

    const hideWineCartSheet = useHideCartSheet(totalWineBottleQty >= MIN_WINE_BOTTLE_QTY_FOR_PERCENT_DISCOUNT_TIER_2);
    const hideGroceryCartSheet = useHideCartSheet(isOrderMinMet);
    const hideFrozenToast = useHideCartSheet(waiveFrozenFee);

    if (!account || !selections) return false;

    return (
        <CartSheetContent
            isFrozenAisleCartSheet={isFrozenAisleRoute && frozenFeeWaiveThreshold != null}
            mobile={mobile}
            hideFrozenToast={hideFrozenToast}
            hideGroceryCartSheet={hideGroceryCartSheet}
            hideWineCartSheet={hideWineCartSheet}
            groceryItemsTotalCost={groceryItemsTotalCost}
            productAdded={productAdded}
            plusMembershipOptInPillMessage={plusMembershipOptInPillMessage}
            plusMembershipOptInPillMessageVariant={plusMembershipOptInPillMessageVariant}
            onPlusMembershipPillDismiss={dismissPlusMembershipOptInPill}
            showPlusMembershipOptInPill={showPlusMembershipOptInPill}
            totalWineBottleQty={totalWineBottleQty}
            showFreeShippingProgress={showFreeShippingProgress}
            showFrozenFeeWaiveToast={showFrozenFeeWaiveToast}
            showOrderMinProgress={showOrderMinProgress}
            unbundleWineFeatureFlag={unbundleWineFeatureFlag}
            winePercentOff={winePercentOff}
        />
    );
}

export default CartSheet;
